import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { subscribeNewsletter } from '../services/firebaseService';
import { checkRateLimit } from '../utils/validation';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const isRTL = i18n.language === 'ar';

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('footer.enterEmail'));
      return;
    }
    if (!checkRateLimit('footer-newsletter', 3, 60000)) {
      toast.error(t('contactPage.tooManyAttempts') || 'Too many attempts. Please wait.');
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success(t('footer.subscribeSuccess'));
      setEmail('');
    } catch (err) {
      if (err.message === 'already_subscribed') {
        toast.error(t('footer.alreadySubscribed'));
      } else {
        toast.error(t('contactPage.errorMsg'));
      }
    } finally {
      setLoading(false);
    }
  };

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const serviceLinks = t('footer.serviceLinks', { returnObjects: true });

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          {/* Brand */}
          <div className="footer-section footer-brand">
            <h3>{t('brand')}</h3>
            <p>{t('footer.description')}</p>
            <div className="social-icons">
              <a href="https://facebook.com/wojodlink" className="social-icon" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook-f" />
              </a>
              <a href="https://twitter.com/wojodlink" className="social-icon" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                <i className="fa-brands fa-x-twitter" />
              </a>
              <a href="https://instagram.com/wojodlink" className="social-icon" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram" />
              </a>
              <a href="https://linkedin.com/company/wojodlink" className="social-icon" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin-in" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section footer-links">
            <h3>{t('footer.quickLinks')}</h3>
            <ul>
              {navLinks.map((l) => (
                <li key={l.to}><Link to={l.to}>{l.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="footer-section footer-links">
            <h3>{t('footer.ourServices')}</h3>
            <ul>
              {serviceLinks.map((s, i) => (
                <li key={i}><Link to="/services">{s}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="footer-section footer-contact">
            <h3>{t('footer.contactUs')}</h3>
            <ul>
              <li><i className="fas fa-phone" /> {t('contactPage.phoneValue')}</li>
              <li><i className="fas fa-envelope" /> {t('contactPage.emailValue')}</li>
              <li><i className="fas fa-globe" /> {t('contactPage.locationValue')}</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="footer-section footer-newsletter">
            <h3>{t('footer.newsletter')}</h3>
            <p>{t('footer.newsletterDesc')}</p>
            <form className="newsletter-form" onSubmit={handleNewsletter}>
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                dir={isRTL ? 'rtl' : 'ltr'}
              />
              <button type="submit" disabled={loading} aria-label="Subscribe">
                <i className={loading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'} />
              </button>
            </form>
          </div>
        </div>

        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </footer>
  );
}
